import { type DragEvent, type ReactElement, useId, useRef, useState } from 'react';
import { Button, cn } from '@/shared/ui';

interface FileDropFieldProps {
  /** 바깥 <Label htmlFor> 와 잇는 input id. 없으면 자동 생성한다. */
  readonly id?: string;
  /** input 의 accept — 끌어다 놓은 파일도 이 형식만 받는다. */
  readonly accept: string;
  readonly multiple?: boolean;
  readonly disabled?: boolean;
  /** 고르거나 끌어다 놓은 파일. 고르기를 취소하면 빈 배열이다 — 호출자가 선택 해제로 다룬다. */
  readonly onFiles: (files: readonly File[]) => void;
  /** 호출자가 들고 있는 현재 선택 파일 이름 — 폼이 제출 때까지 파일을 쥐고 있을 때 보여 준다. */
  readonly fileName?: string | null;
  /** 버튼 글자. 기본 '파일 선택'. */
  readonly buttonLabel?: string;
  /** 버튼 옆 보조 설명. 기본은 끌어다 놓기 안내. */
  readonly hint?: string;
  /** 표 칸처럼 좁은 자리 — 한 줄로 줄인다. */
  readonly compact?: boolean;
  readonly 'aria-label'?: string;
  readonly 'aria-invalid'?: boolean;
  readonly 'aria-describedby'?: string;
}

/** accept(예: "image/jpeg,image/png" · ".woff2,.ttf") 에 맞는 파일만 남긴다 — 끌어다 놓기는 브라우저가 걸러 주지 않는다. */
const matchesAccept = (file: File, accept: string): boolean => {
  const rules = accept
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter((rule) => rule !== '');
  if (rules.length === 0) return true;
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return rules.some((rule) => {
    if (rule.startsWith('.')) return name.endsWith(rule);
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
};

/**
 * 파일 고르기 칸 — 브라우저 기본 "파일 선택 | 선택된 파일 없음" 대신 버튼과 끌어다 놓기 영역을 보인다.
 * 실제 <input type="file"> 은 화면에서 숨기되 id·aria 속성을 그대로 가져, 바깥 Label 과 스펙(getByLabelText)이 전처럼 닿는다.
 * 고른 뒤 input 값을 비워 같은 파일을 다시 골라도 change 가 난다.
 */
export const FileDropField = ({
  id,
  accept,
  multiple = false,
  disabled = false,
  onFiles,
  fileName = null,
  buttonLabel = '파일 선택',
  hint,
  compact = false,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
}: FileDropFieldProps): ReactElement => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);
  const dropHint =
    hint ??
    (multiple ? '또는 파일을 여기로 끌어다 놓으세요' : '또는 파일을 여기로 끌어다 놓으세요');

  /** 고르기 대화상자는 accept 로 이미 거른다 — 그대로 넘겨 호출자의 형식 검사·문구가 전처럼 돈다. */
  const deliverPicked = (files: readonly File[]): void => {
    setRejected(null);
    onFiles(files);
  };

  /** 끌어다 놓기는 브라우저가 거르지 않는다 — accept 에 맞는 것만 넘기고 뺀 파일을 알린다. */
  const deliverDropped = (files: readonly File[]): void => {
    const accepted = files.filter((file) => matchesAccept(file, accept));
    setRejected(
      files.length > 0 && accepted.length === 0
        ? '받을 수 없는 형식의 파일입니다.'
        : accepted.length < files.length
          ? '형식이 맞지 않는 파일은 뺐습니다.'
          : null,
    );
    if (files.length === 0 || accepted.length > 0)
      onFiles(multiple ? accepted : accepted.slice(0, 1));
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    if (disabled) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    if (!isDragging) setIsDragging(true);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    deliverDropped(Array.from(event.dataTransfer.files));
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: 끌어다 놓기 대상일 뿐 누르는 요소가 아니다 — 조작은 안의 버튼·input 이 맡는다.
    <div
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      aria-disabled={disabled}
      className={cn(
        'flex flex-wrap items-center rounded-lg border border-dashed border-input bg-muted/30 transition-colors',
        compact ? 'gap-2 px-2 py-1.5' : 'gap-3 px-4 py-3',
        isDragging && !disabled && 'border-primary bg-primary/5',
        disabled && 'opacity-50',
      )}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedby}
        tabIndex={-1}
        className="sr-only"
        onChange={(event) => {
          deliverPicked(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {buttonLabel}
      </Button>
      {compact ? null : (
        <span className="text-sm text-muted-foreground">
          {fileName !== null ? `고른 파일: ${fileName}` : dropHint}
        </span>
      )}
      {rejected !== null ? (
        <span role="status" className={cn('text-destructive', compact ? 'text-xs' : 'text-sm')}>
          {rejected}
        </span>
      ) : null}
    </div>
  );
};
